/* eslint-disable @ngrx/avoid-duplicate-actions-in-reducer */
import { createEntityAdapter, EntityState } from '@ngrx/entity';
import { createReducer, on } from '@ngrx/store';
import * as baseState from '../../../core/models/base-state/base-state';
import { Pharmacy } from '../../models/pharmacy';
import { PharmacySearchParams } from '../../models/pharmacy-search-params';
import {
  closePharmacySearchDialog,
  getPatientPharmacies,
  getPatientPharmaciesFailure,
  getPatientPharmaciesSuccess,
  getPharmaciesSearch,
  getPharmaciesSearchFailure,
  getPharmaciesSearchSuccess,
  getPharmacyById,
  getPharmacyByIdFailure,
  getPharmacyByIdSuccess,
  navigateToSearchResults,
  nextPage,
  openPharmacySearchDialog,
} from '../actions/pharmacy.actions';

export const pharmacyFeatureKey = 'pharmacies';

export interface PharmacyState extends EntityState<Pharmacy> {
  baseState: baseState.BaseState;
  patientPharmacies: Pharmacy[];
  selectedPharmacy: Pharmacy | null;
  pharmacySearchParams: PharmacySearchParams | null;
}

export const pharmacyAdapter = createEntityAdapter<Pharmacy>({
  selectId: pharmacy => pharmacy.pharmacyId,
});

export const initialState: PharmacyState = pharmacyAdapter.getInitialState({
  baseState: baseState.createBaseState(undefined),
  patientPharmacies: [],
  selectedPharmacy: null,
  pharmacySearchParams: null,
});

export const pharmacyReducer = createReducer<PharmacyState>(
  initialState,

  on(getPharmaciesSearch, (state): PharmacyState => {
    return {
      ...state,
      baseState: {
        ...state.baseState,
        error: null,
        isLoading: true,
      },
    };
  }),

  on(getPharmaciesSearch, openPharmacySearchDialog, (state): PharmacyState => {
    return pharmacyAdapter.removeAll(state);
  }),

  on(getPharmaciesSearchSuccess, (state, { pharmacies }): PharmacyState => pharmacyAdapter.addMany(pharmacies, state)),
  on(getPharmaciesSearchSuccess, (state): PharmacyState => {
    return {
      ...state,
      baseState: {
        ...state.baseState,
        error: null,
        isLoading: false,
      },
    };
  }),

  on(getPharmaciesSearch, (state): PharmacyState => {
    return {
      ...state,
      baseState: {
        ...state.baseState,
        isLoading: true,
        pageNumber: 1,
      },
    };
  }),

  on(getPatientPharmacies, (state): PharmacyState => {
    return {
      ...state,
      patientPharmacies: [],
      baseState: {
        ...state.baseState,
        error: null,
        isLoading: true,
      },
    };
  }),

  on(getPatientPharmaciesSuccess, (state, { patientPharmacies }): PharmacyState => {
    if (patientPharmacies !== null) {
      return {
        ...state,
        patientPharmacies,
        baseState: {
          ...state.baseState,
          error: null,
          isLoading: false,
        },
      };
    }
    return state;
  }),

  on(
    getPatientPharmaciesFailure,
    getPharmaciesSearchFailure,
    getPharmacyByIdFailure,
    (state, { error }): PharmacyState => {
      return {
        ...state,
        baseState: {
          ...state.baseState,
          error,
          isLoading: false,
        },
      };
    }
  ),

  on(nextPage, (state): PharmacyState => {
    const currentPage = state.baseState.pageNumber ? state.baseState.pageNumber + 1 : 1;
    return {
      ...state,
      baseState: {
        ...state.baseState,
        isLoading: true,
        pageNumber: currentPage,
      },
    };
  }),

  on(getPharmacyById, (state): PharmacyState => {
    return {
      ...state,
      selectedPharmacy: null,
      baseState: {
        ...state.baseState,
        error: null,
        isLoading: true,
      },
    };
  }),

  on(getPharmacyByIdSuccess, (state, { pharmacy }): PharmacyState => {
    return {
      ...state,
      selectedPharmacy: pharmacy,
      baseState: {
        ...state.baseState,
        error: null,
        isLoading: false,
      },
    };
  }),

  on(navigateToSearchResults, (state, { searchParams }): PharmacyState => {
    return {
      ...state,
      pharmacySearchParams: searchParams,
    };
  }),

  on(
    closePharmacySearchDialog,
    (state, { selectedPharmacy }): PharmacyState => ({
      ...state,
      selectedPharmacy,
    })
  )
);
